import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, X, Pencil } from "lucide-react";
import { toast } from "sonner";
import { mh } from "@/lib/mh";
import { DeptTag, timeAgo } from "@/components/mh/bits";
import { useAuth } from "@/context/AuthContext";

export const ApprovalCard = ({ item, dept, onDone }) => {
  const { canManage } = useAuth();
  const [busy, setBusy] = useState(false);
  const [mode, setMode] = useState(null);
  const [text, setText] = useState("");
  const act = async (decision, note = "", edited = null) => {
    setBusy(true);
    try {
      await mh.decide(item.id, decision, note, edited);
      toast.success(decision === "approve" ? (edited ? `Approved with your edits · ${item.agent_name} learns from them` : `Approved · ${item.agent_name} will ship`) : `Rejected · ${item.agent_name} stopped`);
      onDone?.(item.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not record decision");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="mh-enter rounded-lg border border-white/[0.07] bg-white/[0.02] p-3.5" data-testid={`approval-${item.id}`}>
      <div className="flex items-center justify-between gap-2">
        <DeptTag name={dept?.name || item.department} hex={dept?.hex || "#fff"} />
        <span className="font-mono text-[10px] text-muted-foreground">{timeAgo(item.created_at)}</span>
      </div>
      {item.agent_slug ? <Link to={`/atlas/${item.agent_slug}`} className="block mt-2 text-sm font-medium hover:underline underline-offset-4">{item.agent_name}</Link> : <div className="mt-2 text-sm font-medium">{item.agent_name}</div>}
      <p className="text-sm text-muted-foreground mt-0.5">wants to ship <span className="text-foreground">{item.artifact}</span></p>
      <div className="flex items-center gap-2 mt-2 font-mono text-[10px] text-muted-foreground">
        <span className="px-1.5 py-0.5 rounded border border-white/10">{item.autonomy}</span>
        <span>conf {Math.round(item.confidence * 100)}%</span>
        <span className="truncate">· {item.reason}</span>
        {item.assignee && <span className="truncate text-[#00E5FF]" data-testid={`approval-assignee-${item.id}`}>· for {item.assignee}</span>}
      </div>
      {mode && (
        <div className="mt-3 space-y-2">
          <textarea value={text} onChange={(e) => setText(e.target.value)} rows={mode === "edit" ? 4 : 2} data-testid={`approval-${mode}-input-${item.id}`}
            placeholder={mode === "edit" ? "Rewrite the output the way it should be" : "Why? (the agent learns from this)"}
            className="w-full rounded-lg bg-white/[0.03] border border-white/10 px-3 py-2 text-sm outline-none focus:border-[#D4FF3A]/60" />
          <div className="flex gap-2">
            <button disabled={busy || !text.trim()} onClick={() => (mode === "edit" ? act("approve", "", text) : act("reject", text))} data-testid={`approval-${mode}-submit-${item.id}`}
              className="flex-1 h-9 rounded-full bg-[#D4FF3A] text-black text-sm font-semibold disabled:opacity-50">{mode === "edit" ? "Approve with edits" : "Reject with reason"}</button>
            <button onClick={() => setMode(null)} className="h-9 px-4 rounded-full border border-white/15 text-sm">Cancel</button>
          </div>
        </div>
      )}
      {!mode && <div className="flex gap-2 mt-3">
        <button disabled={busy || !canManage} onClick={() => { setMode("edit"); setText(item.artifact || ""); }} data-testid={`edit-btn-${item.id}`} title="Edit before approving"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/15 hover:bg-white/5 disabled:opacity-50" aria-label="Edit and approve"><Pencil className="w-4 h-4" /></button>
        <button disabled={busy || !canManage} onClick={() => act("approve")} data-testid={`approve-btn-${item.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full bg-[#D4FF3A] text-black text-sm font-semibold hover:bg-[#e2ff73] active:scale-[0.98] transition-[background-color,transform] disabled:opacity-50">
          <Check className="w-4 h-4" /> Approve
        </button>
        <button disabled={busy || !canManage} onClick={() => act("reject")} data-testid={`reject-btn-${item.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-full border border-white/15 text-sm hover:bg-white/5 hover:border-[#FF3B5C]/60 active:scale-[0.98] transition-[background-color,border-color,transform] disabled:opacity-50">
          <X className="w-4 h-4" /> Reject
        </button>
      </div>}
      {!mode && canManage && <button onClick={() => { setMode("reject"); setText(""); }} className="mt-1.5 font-mono text-[10px] text-muted-foreground hover:text-foreground underline" data-testid={`reject-reason-btn-${item.id}`}>Reject with a reason</button>}
    </div>
  );
};
